import graphql__b32b1c5c44dd7fcfd7d5 from "./__generated__/EmailTemplatesViewEmailTemplatesQuery.graphql.ts";import * as React from "react";
import {useState, useEffect, useCallback} from "react";
import Panel from "./presentational/Panel";
import {usePreloadedQuery} from "react-relay/hooks";
import {graphql} from "react-relay";
import Headline from "./presentational/Headline";
import {faPlusCircle} from "@fortawesome/pro-regular-svg-icons";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import ActionButton from "./presentational/button/ActionButton";
import {connect, ConnectedProps} from "react-redux";
import {hideModal, showModal} from "../store/actions";
import ListSearch from "./presentational/ListSearch";
import EmailTemplateList from "./EmailTemplateList";
import CreateEmailTemplateDialog from "./dialog/CreateEmailTemplateDialog";
import type {EmailTemplatesViewEmailTemplatesQuery as EmailTemplatesViewEmailTemplatesQueryType} from "./__generated__/EmailTemplatesViewEmailTemplatesQuery.graphql";
import {useSearchParams} from "react-router-dom";
import {useOutletContext} from "react-router";
import {useIntl} from "react-intl";

const connector = connect(undefined, {
    dispatchShowModal: showModal,
    dispatchHideModal: hideModal
});

type Props = {};

type EmailTemplatesViewProps = ConnectedProps<typeof connector> & Props;

const EmailTemplatesView: React.FC<EmailTemplatesViewProps> = (props) => {
    const intl = useIntl();
    const prepared: any = useOutletContext();
    const {dispatchShowModal, dispatchHideModal} = props
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
    const [isSearching, setIsSearching] = useState(false);
    const [showUsedOneTime, setShowUsedOneTime] = useState(false);

    const query = usePreloadedQuery<EmailTemplatesViewEmailTemplatesQueryType>(
        graphql__b32b1c5c44dd7fcfd7d5,
        prepared?.emailTemplatesQuery,
    );

    const updateURL = useCallback((term: string) => {
        const newParams = new URLSearchParams(searchParams);
        if (term.trim()) {
            newParams.set('search', term);
        } else {
            newParams.delete('search');
        }
        setSearchParams(newParams, { replace: true });
    }, [searchParams, setSearchParams]);

    const searchAPIDebounced = AwesomeDebouncePromise(
        (searchTerm: string) => {
            setSearchTerm(searchTerm);
            updateURL(searchTerm);
        },
        500
    );

    const handleSearchTermChange = (searchTerm: string) => {
        searchAPIDebounced(searchTerm);
    };

    useEffect(() => {
        const urlSearchTerm = searchParams.get('search') || '';
        setSearchTerm(urlSearchTerm);
    }, [searchParams]);

    // Check if the preloaded query is available
    if (!prepared || !prepared.emailTemplatesQuery) {
        return (
            <Panel>
                <div className="px-6 pt-6 pb-4">
                    <div className="md:flex md:items-center md:justify-between">
                        <div className="flex-1 min-w-0">
                            <Headline>{intl.formatMessage({id: 'view.emailTemplates.headline'})}</Headline>
                        </div>
                    </div>
                </div>
                <div className="text-center py-10">
                    <p className="text-gray-500">{intl.formatMessage({id: 'common.loading'})}</p>
                </div>
            </Panel>
        );
    }

    const showCreateEmailTemplateDialog = () => {
        dispatchShowModal({
            body: (<CreateEmailTemplateDialog handleCloseDialog={dispatchHideModal}/>)
        })
    };

    return (
        <Panel>
            <div className="px-6 pt-6 pb-4">
                <div className="md:flex md:items-center md:justify-between">
                    <div className="flex-1 min-w-0">
                        <Headline>{intl.formatMessage({id: 'view.emailTemplates.headline'})}</Headline>
                    </div>
                    <div className="mt-6 flex space-x-3 md:mt-0 md:ml-4">
                        <ActionButton icon={faPlusCircle} title={intl.formatMessage({id: 'view.emailTemplates.createButton'})} onClick={() => {
                            showCreateEmailTemplateDialog()
                        }}/>
                    </div>
                </div>
                <div className="flex items-center justify-between mt-4">
                    <ListSearch
                        handleSearchTermChange={handleSearchTermChange}
                        placeholder={intl.formatMessage({id: 'view.emailTemplates.searchPlaceholder'})}
                        initialValue={searchTerm}
                        isLoading={isSearching}
                    />
                    <label className="flex items-center gap-2 cursor-pointer ml-4 text-sm text-gray-600">
                        <input
                            type="checkbox"
                            checked={showUsedOneTime}
                            onChange={(e) => setShowUsedOneTime(e.target.checked)}
                            className="h-4 w-4 rounded-sm border-gray-300 text-red-600 focus:ring-red-500"
                        />
                        {intl.formatMessage({id: 'view.emailTemplates.showUsedOneTime'})}
                    </label>
                </div>
            </div>
            <EmailTemplateList
                queryRef={query}
                searchTerm={searchTerm}
                onLoadingChange={setIsSearching}
                showUsedOneTime={showUsedOneTime}
            />
        </Panel>
    )
}

export default connector(EmailTemplatesView);
